<script setup lang="ts">
  import { Floating, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { onBeforeUnmount, watch } from 'vue';
  import { DEPARTURE_CITY_TEXTS } from '../config/texts';
  import type { DepartureCity } from '@/shared/model';

  interface DepartureCityDropdownProps {
    options: DepartureCity[];
    selectedId?: number | null;
    visible: boolean;
  }

  const props = withDefaults(defineProps<DepartureCityDropdownProps>(), {
    selectedId: null,
  });

  interface DepartureCityDropdownEmits {
    (e: 'select', city: DepartureCity): void;
    (e: 'close'): void;
  }

  const emit = defineEmits<DepartureCityDropdownEmits>();

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      emit('close');
    }
  };

  const onFloatingHide = (): void => {
    if (props.visible) {
      emit('close');
    }
  };

  watch(
    () => props.visible,
    (visible) => {
      if (visible) {
        document.addEventListener('keydown', onKeydown);
      } else {
        document.removeEventListener('keydown', onKeydown);
      }
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown);
  });
</script>

<template>
  <Floating
    class="departure-city-dropdown__floating"
    :visible="visible"
    :disable-flip="true"
    full-width
    @hide="onFloatingHide"
  >
    <template #reference>
      <slot name="reference" />
    </template>

    <template #floating>
      <div
        class="departure-city-dropdown"
        data-testid="departure-city-dropdown"
      >
        <div
          v-if="options.length === 0"
          class="departure-city-dropdown__empty"
          data-testid="departure-city-nothing-found"
        >
          <Typo
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ DEPARTURE_CITY_TEXTS.nothingFound }}
          </Typo>
        </div>

        <template v-else>
          <button
            v-for="city in options"
            :key="city.id"
            type="button"
            class="departure-city-dropdown__option"
            :class="{ 'departure-city-dropdown__option_selected': selectedId === city.id }"
            data-testid="departure-city-dropdown-option"
            @click="emit('select', city)"
          >
            <Typo
              :variant="TypoVariant.BodyLRegular"
              tag="span"
            >
              {{ city.name }}
            </Typo>
          </button>
        </template>
      </div>
    </template>
  </Floating>
</template>

<style scoped lang="scss">
  .departure-city-dropdown {
    background-color: var(--color-bg-primary);
    border-radius: var(--rounding-s);
    box-shadow: var(--shadow-s);
    box-sizing: border-box;
    max-height: 360px;
    min-width: 240px;
    overflow-y: auto;
    width: 100%;

    &__option {
      align-items: center;
      background: none;
      border: none;
      color: var(--color-text-primary);
      cursor: pointer;
      display: flex;
      min-height: 40px;
      padding: 0 var(--spacing-m);
      text-align: left;
      width: 100%;

      &_selected {
        background-color: var(--color-neutral-20);
      }

      &:hover {
        background-color: var(--color-bg-secondary);
      }
    }

    &__empty {
      color: var(--color-text-secondary);
      padding: var(--spacing-l) var(--spacing-m);
      text-align: center;
    }
  }
</style>
